import type { DietJsonldDietRead } from "@repo/api/catalog/api/schemas/diet-jsonld-diet-read";
import { addDays, format, min, startOfDay, subDays } from "date-fns";
import type { Locale } from "date-fns/locale";
import { cs, enUS, pl } from "date-fns/locale";
import type {
  DietCatalog,
  DietCatalogAvailabilityType,
  DietCatalogDetail,
} from "@/features/diets/types";
import { calculateAvailabilityDate } from "@/lib/utilities";
import type { LocaleType } from "@/types/locale.type";

export const getDateLocale = (locale: LocaleType): Locale => {
  switch (locale) {
    case "cz":
      return cs;
    case "en":
      return enUS;
    default:
      return pl;
  }
};

export const getDeliveryDate = (date: Date[], locale: LocaleType) => {
  const firstDietDate = date.length > 0 ? min(date) : null;
  const deliveryDate = firstDietDate ? subDays(firstDietDate, 1) : null;
  const formattedDeliveryDate = deliveryDate
    ? `${format(deliveryDate, "eeee", {
        locale: getDateLocale(locale),
      })} ${format(deliveryDate, "dd.MM.yyyy")}`
    : "";

  return formattedDeliveryDate;
};

export class DietDateManager {
  private readonly firstPossibleOrderDay: Date;
  private readonly disabledDays: Date[];
  private readonly saturdays: boolean;
  private readonly sundays: boolean;
  readonly lastPossibleOrderDay: Date | undefined;
  readonly lastPossibleOrderDayFormatted: string;
  readonly dateLocale: Locale;

  constructor(config: {
    diet: DietCatalogDetail | DietCatalog | DietJsonldDietRead;
    locale: LocaleType;
    saturdays: boolean;
    sundays: boolean;
  }) {
    const { diet, locale, saturdays, sundays } = config;
    this.dateLocale = getDateLocale(locale);
    this.firstPossibleOrderDay = calculateAvailabilityDate(
      diet.availability as DietCatalogAvailabilityType,
      "date",
      locale
    ) as Date;
    this.disabledDays =
      diet.catering?.disabledDays?.map((day) => new Date(day)) ?? [];
    this.saturdays = saturdays;
    this.sundays = sundays;
    this.lastPossibleOrderDay = diet.catering?.maximumDaysAllowedToOrderInFuture
      ? addDays(
          new Date(),
          diet.catering?.maximumDaysAllowedToOrderInFuture ?? 0
        )
      : undefined;
    this.lastPossibleOrderDayFormatted = this.lastPossibleOrderDay
      ? format(this.lastPossibleOrderDay, "dd.MM.yyyy")
      : "";
  }

  private isDisabledDate(day: Date): boolean {
    if (!this.saturdays && day.getDay() === 6) return true;
    if (!this.sundays && day.getDay() === 0) return true;
    if (
      this.disabledDays.find(
        (date) => date.toDateString() === day.toDateString()
      )
    )
      return true;
    if (
      this.firstPossibleOrderDay &&
      startOfDay(day) < startOfDay(this.firstPossibleOrderDay)
    )
      return true;
    return false;
  }

  findNextAvailableDate(date: Date): Date {
    const nextDay = addDays(date, 1);
    if (!this.isDisabledDate(nextDay)) return nextDay;
    return this.findNextAvailableDate(nextDay);
  }

  findNextAvailableDatesArray(date: Date, count: number): Date[] {
    const nextDays: Date[] = [];
    let nextDay = date;
    for (let i = 0; i < count; i++) {
      nextDay = this.findNextAvailableDate(nextDay);
      nextDays.push(nextDay);
    }
    return nextDays;
  }

  areDatesAvailable(dates: Date[]): boolean {
    return dates.every(
      (date) =>
        !this.isDisabledDate(date) &&
        !(
          this.lastPossibleOrderDay &&
          startOfDay(date) > startOfDay(this.lastPossibleOrderDay)
        )
    );
  }

  formatNextAvailableDateEeee(date: Date): string {
    return format(this.findNextAvailableDate(date), "eeee", {
      locale: this.dateLocale,
    });
  }
}
